import { useEffect, useState } from 'react'

import CodeIcon from '@mui/icons-material/Code'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArchiveIcon from '@mui/icons-material/Archive'
import DownloadIcon from '@mui/icons-material/Download'
import EditIcon from '@mui/icons-material/Edit'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'

import { useAuth } from '../../auth/hooks/useAuth'
import { ArticleStatusChip } from '../components/ArticleStatusChip'
import { MarkdownContent } from '../components/MarkdownContent'
import { RichContent } from '../components/RichContent'
import { articleApi } from '../services/articleApi'
import type { Article, ArticleMarkdownPreview } from '../types/article.types'
import type { ApiError, SnackbarState } from '../../../shared/types/common'

export function ArticleDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [article, setArticle] = useState<Article | null>(null)
  const [markdownPreview, setMarkdownPreview] = useState<ArticleMarkdownPreview | null>(null)
  const [markdownLoading, setMarkdownLoading] = useState(true)
  const [markdownError, setMarkdownError] = useState('')
  const [markdownTab, setMarkdownTab] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [workflowUpdating, setWorkflowUpdating] = useState(false)
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false)
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  })

  useEffect(() => {
    if (!id) {
      setError('Article not found or access denied')
      setLoading(false)
      return
    }

    articleApi
      .get(id)
      .then((response) => setArticle(response.data))
      .catch(() => setError('Article not found or access denied'))
      .finally(() => setLoading(false))

    setMarkdownLoading(true)
    setMarkdownError('')
    articleApi
      .getMarkdownPreview(id)
      .then((response) => setMarkdownPreview(response.data))
      .catch(() => setMarkdownError('Unable to generate Wiki.js markdown preview'))
      .finally(() => setMarkdownLoading(false))
  }, [id])

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>
  }

  if (!article) {
    return null
  }

  const tags: string[] = (() => {
    try {
      return article.tags ? (JSON.parse(article.tags) as string[]) : []
    } catch {
      return []
    }
  })()

  const normalizedUserRole = String(user?.role || '').trim().toLowerCase()
  const normalizedArticleState = String(article.state || '').trim().toLowerCase()

  const canEdit = Boolean(
    user &&
      (user.id === article.author_id || ['admin', 'publisher'].includes(normalizedUserRole)) &&
      ['draft', 'changes_requested'].includes(normalizedArticleState)
  )
  const canReturnToDraft = Boolean(
    user && ['admin', 'publisher'].includes(normalizedUserRole) && ['approved', 'published'].includes(normalizedArticleState)
  )
  const canArchivePublished = Boolean(user && normalizedUserRole === 'admin' && normalizedArticleState === 'published')

  async function handleRollbackToDraft() {
    if (!id) {
      return
    }

    setWorkflowUpdating(true)
    try {
      const response = await articleApi.rollbackToDraft(id)
      setArticle(response.data)
      setSnackbar({
        open: true,
        message: 'Article returned to draft. Edit it and complete the workflow again before publishing.',
        severity: 'success',
      })
    } catch (rollbackError) {
      setSnackbar({
        open: true,
        message:
          (rollbackError as ApiError).response?.data?.message || 'Failed to return article to draft',
        severity: 'error',
      })
    } finally {
      setWorkflowUpdating(false)
    }
  }

  async function handleArchivePublished() {
    if (!id) {
      return
    }

    setWorkflowUpdating(true)
    try {
      const response = await articleApi.archive(id)
      setArticle(response.data)
      setArchiveDialogOpen(false)
      setSnackbar({
        open: true,
        message: 'Technote archived and removed from Wiki.js access.',
        severity: 'success',
      })
    } catch (archiveError) {
      setSnackbar({
        open: true,
        message:
          (archiveError as ApiError).response?.data?.message || 'Failed to archive technote',
        severity: 'error',
      })
    } finally {
      setWorkflowUpdating(false)
    }
  }

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Paper sx={{ p: 3, borderLeft: '4px solid #0f4c81' }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
          <Box>
            <Typography variant="h5" mb={1}>
              {article.title}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <ArticleStatusChip state={article.state} />
              {article.category && (
                <Chip
                  label={article.category}
                  variant="outlined"
                  size="small"
                  sx={{ borderColor: '#d8e0ea', color: '#6b7280' }}
                />
              )}
              {tags.map((tag) => (
                <Chip key={tag} label={tag} size="small" />
              ))}
            </Stack>
          </Box>
          <Box display="flex" gap={1} flexShrink={0}>
            {canReturnToDraft && (
              <Button
                variant="contained"
                color="warning"
                disabled={workflowUpdating}
                onClick={() => void handleRollbackToDraft()}
              >
                Return To Draft
              </Button>
            )}
            {canArchivePublished && (
              <Button
                variant="contained"
                color="error"
                startIcon={<ArchiveIcon />}
                disabled={workflowUpdating}
                onClick={() => setArchiveDialogOpen(true)}
              >
                Archive
              </Button>
            )}
            {canEdit && (
              <Button variant="contained" startIcon={<EditIcon />} onClick={() => navigate(`/articles/${id}/edit`)}>
                Edit
              </Button>
            )}
            <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
              Back
            </Button>
          </Box>
        </Box>

        <Box mt={2} pt={2} sx={{ borderTop: '1px solid #d8e0ea', display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary">
            <strong>Author:</strong> {article.author_name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            <strong>Created:</strong> {article.created_at ? new Date(article.created_at).toLocaleString() : '—'}
          </Typography>
          {article.approved_by_name && (
            <Typography variant="caption" color="text.secondary">
              <strong>Approved by:</strong> {article.approved_by_name}
            </Typography>
          )}
          {article.published_by_name && (
            <Typography variant="caption" color="text.secondary">
              <strong>Published by:</strong> {article.published_by_name}
            </Typography>
          )}
          {article.wikijs_page_url && (
            <Typography variant="caption" color="text.secondary">
              <strong>Wiki.js URL:</strong>{' '}
              <a href={article.wikijs_page_url} target="_blank" rel="noreferrer">
                {article.wikijs_page_url}
              </a>
            </Typography>
          )}
        </Box>
      </Paper>

      <Paper sx={{ overflow: 'hidden' }}>
        <Accordion defaultExpanded={normalizedUserRole === 'admin' || normalizedUserRole === 'publisher'} elevation={0} disableGutters>
          <AccordionSummary expandIcon={<CodeIcon fontSize="small" />}>
            <Box>
              <Typography variant="subtitle1">Wiki.js Markdown Preview</Typography>
              <Typography variant="body2" color="text.secondary">
                Preview the exact markdown that will be sent to Wiki.js on publish.
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            {markdownLoading ? (
              <Box display="flex" justifyContent="center" py={3}>
                <CircularProgress size={24} />
              </Box>
            ) : markdownError ? (
              <Alert severity="error">{markdownError}</Alert>
            ) : (
              <Box>
                <Tabs
                  value={markdownTab}
                  onChange={(_, nextTab) => setMarkdownTab(nextTab)}
                  sx={{ mb: 2, borderBottom: '1px solid #d8e0ea' }}
                >
                  <Tab label="Rendered Preview" sx={{ textTransform: 'none', fontWeight: 700 }} />
                  <Tab label="Raw Markdown" sx={{ textTransform: 'none', fontWeight: 700 }} />
                </Tabs>

                {markdownTab === 0 ? (
                  <Box
                    sx={{
                      p: 2.5,
                      backgroundColor: '#ffffff',
                      border: '1px solid #d8e0ea',
                      borderRadius: 2,
                    }}
                  >
                    <MarkdownContent
                      markdown={markdownPreview?.markdown}
                      emptyFallback="No markdown preview available"
                    />
                  </Box>
                ) : (
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 2,
                      overflowX: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                      fontSize: 13,
                      lineHeight: 1.6,
                      backgroundColor: '#f8fafc',
                      border: '1px solid #d8e0ea',
                      borderRadius: 2,
                    }}
                  >
                    {markdownPreview?.markdown || 'No preview available'}
                  </Box>
                )}
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      </Paper>

      {article.attachments && article.attachments.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="subtitle1" mb={2}>
            Attachments
          </Typography>
          <List dense disablePadding>
            {article.attachments.map((attachment) => (
              <ListItem
                key={attachment.id}
                sx={{ justifyContent: 'space-between' }}
                secondaryAction={
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    href={attachment.signed_url || articleApi.downloadAttachment(article.id, attachment.id)}
                    target="_blank"
                  >
                    Download
                  </Button>
                }
              >
                <ListItemText
                  primary={<Typography variant="body2" fontWeight={700}>{attachment.original_name}</Typography>}
                  secondary={attachment.mime_type}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {article.auditLog && article.auditLog.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="subtitle1" mb={2}>
            Audit Log
          </Typography>
          <List dense disablePadding>
            {article.auditLog.map((log) => (
              <ListItem key={log.id}>
                <ListItemText
                  primary={<Typography variant="body2" fontWeight={600}>{log.user_name} — {log.action}</Typography>}
                  secondary={new Date(log.created_at).toLocaleString()}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      <Dialog open={archiveDialogOpen} onClose={() => setArchiveDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Archive published technote?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This removes the published Wiki.js page from access first, then keeps the technote archived in the portal with its history and audit records.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveDialogOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={workflowUpdating} onClick={() => void handleArchivePublished()}>
            Archive Technote
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  )
}
